import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings as SettingsIcon, 
  Building2, 
  Users, 
  CreditCard, 
  Bell, 
  Shield, 
  Palette,
  Key,
  Mail,
  Phone,
  Globe,
  Save,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Calendar,
  Plus,
  Edit,
  DollarSign
} from 'lucide-react';

const Settings = () => {
  const [organizationSettings, setOrganizationSettings] = useState({
    name: 'Legacy Property Association',
    email: 'admin@legacyproperty.com',
    phone: '+1 (555) 123-4567',
    website: 'https://legacyproperty.com',
    address: '123 Business St, City, State 12345',
    timezone: 'America/New_York'
  });

  const [integrationSettings, setIntegrationSettings] = useState({
    stripe: { enabled: true, status: 'connected' },
    paypal: { enabled: false, status: 'not_configured' },
    zelle: { enabled: false, status: 'not_configured' },
    docusign: { enabled: false, status: 'not_configured' },
    twilio: { enabled: true, status: 'connected' }
  });

  const [paymentConfig, setPaymentConfig] = useState({
    stripe_enabled: false,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    paypal_enabled: false,
    paypal_client_id: '',
    paypal_client_secret: '',
    zelle_enabled: false,
    zelle_email: '',
    zelle_phone: '',
    use_platform_default: true,
    stripe_configured: false,
    paypal_configured: false,
    zelle_configured: false
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalType, setPaymentModalType] = useState('');

  // Load payment configuration on component mount
  useEffect(() => {
    fetchPaymentConfig();
  }, []);

  const fetchPaymentConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landlord/payment-config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
        }
      });
      if (response.ok) {
        const config = await response.json();
        setPaymentConfig(config);
        
        // Update integration settings based on actual configuration
        setIntegrationSettings(prev => ({
          ...prev,
          stripe: { 
            enabled: config.stripe_enabled, 
            status: config.stripe_configured ? 'connected' : 'not_configured' 
          },
          paypal: { 
            enabled: config.paypal_enabled, 
            status: config.paypal_configured ? 'connected' : 'not_configured' 
          },
          zelle: { 
            enabled: config.zelle_enabled, 
            status: config.zelle_configured ? 'connected' : 'not_configured' 
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching payment config:', error);
    }
  };

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: true,
    paymentReminders: true,
    maintenanceAlerts: true,
    leaseExpirations: true,
    overduePayments: true
  });

  const [userSettings, setUserSettings] = useState({
    language: 'en',
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    theme: 'light'
  });

  const handleSave = (section) => {
    // TODO: Implement save functionality
    console.log(`Saving ${section} settings`);
  };

  const handlePaymentConfigSave = async (configData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landlord/payment-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
        },
        body: JSON.stringify(configData)
      });

      if (response.ok) {
        await fetchPaymentConfig(); // Refresh the configuration
        setShowPaymentModal(false);
        // Show success message
        console.log('Payment configuration saved successfully');
      } else {
        const error = await response.json();
        console.error('Error saving payment config:', error);
        alert(`Error: ${error.detail || 'Failed to save payment configuration'}`);
      }
    } catch (error) {
      console.error('Error saving payment config:', error);
      alert('Error saving payment configuration');
    }
  };

  const PaymentConfigModal = ({ type, config, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      stripe_enabled: config.stripe_enabled,
      stripe_secret_key: config.stripe_secret_key || '',
      stripe_publishable_key: config.stripe_publishable_key || '',
      paypal_enabled: config.paypal_enabled,
      paypal_client_id: config.paypal_client_id || '',
      paypal_client_secret: config.paypal_client_secret || ''
    });
    const [loading, setLoading] = useState(false);

    // Load current configuration when modal opens
    useEffect(() => {
      const loadCurrentConfig = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landlord/payment-config`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
            }
          });
          
          if (response.ok) {
            const config = await response.json();
            setFormData(prev => ({
              ...prev,
              stripe_enabled: config.stripe_enabled || false,
              stripe_publishable_key: config.stripe_publishable_key || '',
              paypal_enabled: config.paypal_enabled || false,
              paypal_client_id: config.paypal_client_id || ''
            }));
          }
        } catch (error) {
          console.error('Error loading payment configuration:', error);
        }
      };
      
      loadCurrentConfig();
    }, []);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      
      try {
        // Prepare data based on which type we're configuring
        const configData = { ...formData };
        
        if (type === 'stripe') {
          configData.stripe_enabled = true;
        } else if (type === 'paypal') {
          configData.paypal_enabled = true;
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landlord/payment-config`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
          },
          body: JSON.stringify(configData)
        });

        if (response.ok) {
          // Refresh integration status
          await fetchPaymentConfig();
          onClose();
        } else {
          const errorData = await response.json();
          console.error('Failed to save configuration:', errorData);
          alert(`Error: ${errorData.detail || 'Failed to save configuration'}`);
        }
      } catch (error) {
        console.error('Error saving configuration:', error);
        alert('Error saving configuration. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const handleDisable = async () => {
      setLoading(true);
      
      try {
        const configData = { ...formData };
        
        if (type === 'stripe') {
          configData.stripe_enabled = false;
          configData.stripe_secret_key = '';
          configData.stripe_publishable_key = '';
        } else if (type === 'paypal') {
          configData.paypal_enabled = false;
          configData.paypal_client_id = '';
          configData.paypal_client_secret = '';
        } else if (type === 'zelle') {
          configData.zelle_enabled = false;
          configData.zelle_email = '';
          configData.zelle_phone = '';
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landlord/payment-config`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
          },
          body: JSON.stringify(configData)
        });

        if (response.ok) {
          await fetchPaymentConfig();
          onClose();
        } else {
          console.error('Failed to disable configuration');
        }
      } catch (error) {
        console.error('Error disabling configuration:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">Configure {type.charAt(0).toUpperCase() + type.slice(1)}</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {type === 'stripe' && (
              <>
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Enter your own Stripe credentials. Each landlord must configure their own payment processing.
                  </p>
                </div>
                
                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    id="stripe_enabled"
                    checked={formData.stripe_enabled}
                    onChange={(e) => setFormData({...formData, stripe_enabled: e.target.checked})}
                  />
                  <Label htmlFor="stripe_enabled">Enable Stripe payments</Label>
                </div>

                <div>
                  <Label htmlFor="stripe_secret_key">Stripe Secret Key *</Label>
                  <Input
                    id="stripe_secret_key"
                    type="password"
                    placeholder="sk_live_... or sk_test_..."
                    value={formData.stripe_secret_key}
                    onChange={(e) => setFormData({...formData, stripe_secret_key: e.target.value})}
                    required={formData.stripe_enabled}
                  />
                </div>
                <div>
                  <Label htmlFor="stripe_publishable_key">Stripe Publishable Key *</Label>
                  <Input
                    id="stripe_publishable_key"
                    type="text"
                    placeholder="pk_live_... or pk_test_..."
                    value={formData.stripe_publishable_key}
                    onChange={(e) => setFormData({...formData, stripe_publishable_key: e.target.value})}
                    required={formData.stripe_enabled}
                  />
                </div>
              </>
            )}

            {type === 'paypal' && (
              <>
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Enter your own PayPal credentials. Each landlord must configure their own payment processing.
                  </p>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    id="paypal_enabled"
                    checked={formData.paypal_enabled}
                    onChange={(e) => setFormData({...formData, paypal_enabled: e.target.checked})}
                  />
                  <Label htmlFor="paypal_enabled">Enable PayPal payments</Label>
                </div>

                <div>
                  <Label htmlFor="paypal_client_id">PayPal Client ID *</Label>
                  <Input
                    id="paypal_client_id"
                    type="text"
                    placeholder="Your PayPal Client ID"
                    value={formData.paypal_client_id}
                    onChange={(e) => setFormData({...formData, paypal_client_id: e.target.value})}
                    required={formData.paypal_enabled}
                  />
                </div>
                <div>
                  <Label htmlFor="paypal_client_secret">PayPal Client Secret *</Label>
                  <Input
                    id="paypal_client_secret"
                    type="password"
                    placeholder="Your PayPal Client Secret"
                    value={formData.paypal_client_secret}
                    onChange={(e) => setFormData({...formData, paypal_client_secret: e.target.value})}
                    required={formData.paypal_enabled}
                  />
                </div>
              </>
            )}

            {type === 'zelle' && (
              <>
                <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-800">
                    Configure your Zelle account for receiving rent payments. Tenants will send payments directly via their bank's Zelle.
                  </p>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    id="zelle_enabled"
                    checked={formData.zelle_enabled}
                    onChange={(e) => setFormData({...formData, zelle_enabled: e.target.checked})}
                  />
                  <Label htmlFor="zelle_enabled">Enable Zelle payments</Label>
                </div>

                <div className="space-y-4">
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      💡 Provide at least one: Email OR Phone number registered with Zelle
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="zelle_email">Zelle Email Address</Label>
                    <Input
                      id="zelle_email"
                      type="email"
                      placeholder="your-email@example.com"
                      value={formData.zelle_email}
                      onChange={(e) => setFormData({...formData, zelle_email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="zelle_phone">Zelle Phone Number</Label>
                    <Input
                      id="zelle_phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.zelle_phone}
                      onChange={(e) => setFormData({...formData, zelle_phone: e.target.value})}
                    />
                  </div>

                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-xs text-gray-600">
                      ℹ️ <strong>Note:</strong> Zelle payments are free with no transaction fees. You'll need to manually verify and record payments in the system.
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="flex space-x-3 pt-4">
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? 'Saving...' : 'Save Configuration'}
              </Button>
              {(type === 'stripe' ? formData.stripe_enabled : type === 'paypal' ? formData.paypal_enabled : formData.zelle_enabled) && (
                <Button type="button" variant="destructive" onClick={handleDisable} disabled={loading}>
                  Disable
                </Button>
              )}
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const IntegrationCard = ({ name, icon: Icon, status, enabled, description }) => (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Icon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{name}</h3>
              <p className="text-gray-600 text-sm">{description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge 
              variant={status === 'connected' ? 'default' : status === 'not_configured' ? 'secondary' : 'destructive'}
              className={
                status === 'connected' 
                  ? 'bg-green-100 text-green-800' 
                  : status === 'not_configured'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }
            >
              {status === 'connected' ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Connected
                </>
              ) : status === 'not_configured' ? (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Not Configured
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Error
                </>
              )}
            </Badge>
            <Button 
              variant={status === 'connected' ? 'outline' : 'default'} 
              size="sm"
              onClick={() => {
                setPaymentModalType(name.toLowerCase());
                setShowPaymentModal(true);
              }}
            >
              {status === 'connected' ? 'Manage' : 'Configure'}
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your organization settings and integrations</p>
      </div>

      <Tabs defaultValue="organization" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="autopay">Autopay & Collections</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        {/* Organization Settings */}
        <TabsContent value="organization" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                Organization Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input 
                    id="orgName" 
                    value={organizationSettings.name}
                    onChange={(e) => setOrganizationSettings({...organizationSettings, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="orgEmail">Email</Label>
                  <Input 
                    id="orgEmail" 
                    type="email"
                    value={organizationSettings.email}
                    onChange={(e) => setOrganizationSettings({...organizationSettings, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="orgPhone">Phone</Label>
                  <Input 
                    id="orgPhone" 
                    value={organizationSettings.phone}
                    onChange={(e) => setOrganizationSettings({...organizationSettings, phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="orgWebsite">Website</Label>
                  <Input 
                    id="orgWebsite" 
                    value={organizationSettings.website}
                    onChange={(e) => setOrganizationSettings({...organizationSettings, website: e.target.value})}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="orgAddress">Address</Label>
                  <Input 
                    id="orgAddress" 
                    value={organizationSettings.address}
                    onChange={(e) => setOrganizationSettings({...organizationSettings, address: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleSave('organization')}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Key className="h-5 w-5 mr-2" />
                Payment Integrations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <IntegrationCard 
                name="Stripe"
                icon={CreditCard}
                status={integrationSettings.stripe.status}
                enabled={integrationSettings.stripe.enabled}
                description="Process credit card payments and ACH transfers"
              />
              <IntegrationCard 
                name="PayPal"
                icon={Globe}
                status={integrationSettings.paypal.status}
                enabled={integrationSettings.paypal.enabled}
                description="Accept PayPal payments from tenants"
              />
              <IntegrationCard 
                name="Zelle"
                icon={DollarSign}
                status={integrationSettings.zelle?.status || 'not_configured'}
                enabled={integrationSettings.zelle?.enabled || false}
                description="Accept Zelle payments - No transaction fees"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="h-5 w-5 mr-2" />
                Communication Integrations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <IntegrationCard 
                name="Twilio SMS"
                icon={Phone}
                status={integrationSettings.twilio.status}
                enabled={integrationSettings.twilio.enabled}
                description="Send SMS notifications to tenants and contractors"
              />
              <IntegrationCard 
                name="DocuSign"
                icon={Shield}
                status={integrationSettings.docusign.status}
                enabled={integrationSettings.docusign.enabled}
                description="Electronic signature for lease agreements"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Autopay & Collections */}
        <TabsContent value="autopay" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Autopay Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">0</div>
                  <div className="text-sm text-blue-800">Active Autopay Tenants</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">$0</div>
                  <div className="text-sm text-green-800">Monthly Autopay Volume</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">0</div>
                  <div className="text-sm text-yellow-800">Failed Autopay Attempts</div>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-3">Quick Actions</h4>
                <div className="flex space-x-3">
                  <Button variant="outline" size="sm">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Process Monthly Autopay
                  </Button>
                  <Button variant="outline" size="sm">
                    <Bell className="h-4 w-4 mr-2" />
                    Send Payment Reminders
                  </Button>
                  <Button variant="outline" size="sm">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Apply Late Fees
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Late Fee Rules
                </div>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Default Late Fee Rule</h4>
                      <p className="text-sm text-gray-600">$50 flat fee after 5 days grace period</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Badge variant="secondary">Active</Badge>
                    </div>
                  </div>
                </div>
                
                <div className="text-center py-8 text-gray-500">
                  <p>No additional late fee rules configured</p>
                  <Button variant="link" className="mt-2">
                    <Plus className="h-4 w-4 mr-1" />
                    Create Property-Specific Rule
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="h-5 w-5 mr-2" />
                Payment Reminders
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reminderDays">Days Before Due Date</Label>
                  <select className="w-full p-2 border border-gray-300 rounded-md">
                    <option value="3">3 days before</option>
                    <option value="5">5 days before</option>
                    <option value="7">7 days before</option>
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="overdueReminder">Days After Due Date</Label>
                  <select className="w-full p-2 border border-gray-300 rounded-md">
                    <option value="1">1 day after</option>
                    <option value="3">3 days after</option>
                    <option value="7">7 days after</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="emailReminders" className="rounded" defaultChecked />
                  <Label htmlFor="emailReminders">Email Reminders</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="smsReminders" className="rounded" />
                  <Label htmlFor="smsReminders">SMS Reminders</Label>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button>
                  <Save className="h-4 w-4 mr-2" />
                  Save Reminder Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="h-5 w-5 mr-2" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email-notifications">Email Notifications</Label>
                    <p className="text-sm text-gray-600">Receive notifications via email</p>
                  </div>
                  <Switch 
                    id="email-notifications"
                    checked={notificationSettings.emailNotifications}
                    onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, emailNotifications: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="sms-notifications">SMS Notifications</Label>
                    <p className="text-sm text-gray-600">Receive notifications via SMS</p>
                  </div>
                  <Switch 
                    id="sms-notifications"
                    checked={notificationSettings.smsNotifications}
                    onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, smsNotifications: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="payment-reminders">Payment Reminders</Label>
                    <p className="text-sm text-gray-600">Automatic reminders for overdue payments</p>
                  </div>
                  <Switch 
                    id="payment-reminders"
                    checked={notificationSettings.paymentReminders}
                    onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, paymentReminders: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="maintenance-alerts">Work Order Alerts</Label>
                    <p className="text-sm text-gray-600">Notifications for new work orders</p>
                  </div>
                  <Switch 
                    id="maintenance-alerts"
                    checked={notificationSettings.maintenanceAlerts}
                    onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, maintenanceAlerts: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="lease-expirations">Lease Expiration Alerts</Label>
                    <p className="text-sm text-gray-600">Reminders for upcoming lease expirations</p>
                  </div>
                  <Switch 
                    id="lease-expirations"
                    checked={notificationSettings.leaseExpirations}
                    onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, leaseExpirations: checked})}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleSave('notifications')}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences */}
        <TabsContent value="preferences" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Palette className="h-5 w-5 mr-2" />
                Display Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="language">Language</Label>
                  <select 
                    id="language" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={userSettings.language}
                    onChange={(e) => setUserSettings({...userSettings, language: e.target.value})}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <select 
                    id="currency" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={userSettings.currency}
                    onChange={(e) => setUserSettings({...userSettings, currency: e.target.value})}
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="dateFormat">Date Format</Label>
                  <select 
                    id="dateFormat" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={userSettings.dateFormat}
                    onChange={(e) => setUserSettings({...userSettings, dateFormat: e.target.value})}
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="theme">Theme</Label>
                  <select 
                    id="theme" 
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={userSettings.theme}
                    onChange={(e) => setUserSettings({...userSettings, theme: e.target.value})}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="system">System</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleSave('preferences')}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input id="currentPassword" type="password" />
                </div>
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input id="newPassword" type="password" />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input id="confirmPassword" type="password" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleSave('security')}>
                  <Save className="h-4 w-4 mr-2" />
                  Update Password
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>API Keys & Tokens</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                    <span className="font-medium text-yellow-800">Security Notice</span>
                  </div>
                  <p className="text-sm text-yellow-700 mt-2">
                    API keys and sensitive configuration should be managed through your platform's secure environment variables.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Configuration Modal */}
      {showPaymentModal && (
        <PaymentConfigModal 
          type={paymentModalType}
          config={paymentConfig}
          onClose={() => setShowPaymentModal(false)}
          onSave={handlePaymentConfigSave}
        />
      )}
    </div>
  );
};

export default Settings;